import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { apiRetry } from "../../../../utils";
import {
  Segment,
  Tab,
  TabPane,
  Header,
  Grid,
  GridColumn,
  Table,
  Dropdown,
  Confirm,
  Icon,
  Popup,
} from "semantic-ui-react";
import { Pie, Column,DualAxes } from "@ant-design/plots";
import { Link, useLocation } from "react-router-dom";
import { Form, Permission, Productionform, Socialform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import "./SocialKPIsDashBoard.scss";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { CollapsibleSection, LoadingSpinner } from "../../../Shared";
import SocialSummary from "./SocialSummary";
import TableGraf from "./TableGraf";
import KPIExportIconButton from "../KPIExportIconButton";
import {
  socialKPIsCodes,
  socialFormulasIndicators,
  determineSocialUnits,
  socialCodes,
} from "../../../../utils/dataAndFormulas/socialForms";

import {
  convertUnits,
  formatValue,
} from "../../../../utils/dataAndFormulas/convert";
import { formatWhole, formatMagnitude, formatMagnitudeAdaptive } from "../../../../utils/numberFormat";
import {
  fetchProductionMonthlyData,
  buildProductionComparisonData,
  buildWorkersComparisonData,
  buildProductivityData,
} from "./socialChartData";
import { drawSocialCustomCharts } from "./pdfSocialCharts";

const permissionController = new Permission();
const socialController = new Socialform();
const formController = new Form();
const productionController = new Productionform();

export function SocialKPIsDashBoard(props) {
  const { site, sharedProductionForms, sharedWorkersByPeriod, siteName = "" } = props;
  const {
    user: { role },
    accessToken,
  } = useAuth();
  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key;

  const [permissionActive, setPermissionsActive] = useState([]);
  const [baseline, setbaseline] = useState("");
  const [valueBaseline, setValueBaseline] = useState({});
  const [missingBaselineYears, setMissingBaselineYears] = useState([]);
  const [indicatorsLineBase, setIndicatorsLineBase] = useState([]);
  const [data, setData] = useState([]);
  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [configFormData, setConfigFormData] = useState(null);

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;

    const [lastYears, setLastYears] = useState({}); // resultados calculados
    const [totalesByFilters, setTotalesByFilters] = useState({}); // resultados calculados
    const [totalesWorkersByFilters, setTotalesWorkersByFilters] = useState({}); // resultados de trabajadores por mes
    const [totalesProductionsByFilters, setTotalesProductionsByFilters] = useState({}); // resultados de producción por mes

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));
  
    //declaración estado de filtros
    const [filters, setFilters] = useState({
      year: new Date().getFullYear(),
      //month: new Date().getMonth() + 1,
    });

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getMyPermissions(accessToken, true);
          setPermissionsActive(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsActive([]);
      } finally {
        stopLoading();
      }
    })();
  }, []);

useEffect(() => {
  if (!site) return;

  const fetchData = async () => {
    startLoading();
    setData([]);
    setbaseline("");
    try {
      // --- 1. Obtener años de línea base y últimos 5 años ---
      const prueba = await apiRetry(() => socialController.getSocialFormsByLineBaseYears(accessToken, site));

      setIndicatorsLineBase(prueba.socialForms.indicators);

      const yearsLineaBase = prueba.socialForms.years;
      const currentYear = new Date().getFullYear();
      const yearsLast5 = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

      const yearsCombined = [...new Set([...yearsLineaBase, ...yearsLast5])];

      // --- 2. Obtener datos de socialForms ---
      const responseSocial = await apiRetry(() => socialController.getSocialFormsByLastYears(accessToken, site, yearsCombined));
      setData(responseSocial.socialForms);

      // --- 3. Obtener baseline ---
      const responseBaseline = await apiRetry(() =>
        formController.getLastBaselineByFormBySite(
          accessToken,
          site,
          "production"
        )
      );

      if (responseBaseline.code === 200) {
        setbaseline(responseBaseline.baseline);

        const result = await Promise.all(
          Object.entries(responseBaseline.baseline)
            .filter(([anio, valor]) => valor !== null)
            .map(async ([anio, valor]) => {
              const a = await getValueBaseLine(anio, valor);
              return { [anio]: a };
            })
        );

        const resultadoFinal = result.reduce((acc, obj) => ({ ...acc, ...obj }), {});
        setValueBaseline(resultadoFinal);
      }
    } catch (error) {
      console.error(error);
      setData([]);
    } finally {
      stopLoading();
    }
  };

  fetchData();
}, [site]);

  // Obtener configuración de campos activos del formulario
  useEffect(() => {
    (async () => {
      try {
        if (site) {
          const response = await formController.getFormsBySite(
            accessToken,
            site,
            "social",
            true
          );
          if (Array.isArray(response) && response.length > 0) {
            setConfigFormData(response[0]);
          } else {
            setConfigFormData(null);
          }
        }
      } catch (error) {
        console.error("Error al obtener configFormData:", error);
        setConfigFormData(null);
      }
    })();
  }, [site]);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        // Usar datos compartidos desde DashBoard.js
        if (site && sharedWorkersByPeriod && sharedProductionForms) {
          setTotalesWorkersByFilters(sharedWorkersByPeriod);

          const totalesFinalProductions = await calcularProduccionPorMesPorAño(sharedProductionForms, baseline);
          setTotalesProductionsByFilters(totalesFinalProductions);
        }

        const previous_totals = await transformData1(data);
        await calculateDataFinal(previous_totals);

        // Detectar años sin baseline para mostrar aviso
        const currentYear = new Date().getFullYear();
        const yearsToCheck = [0,1,2,3,4].map(i => String(currentYear - i));
        setMissingBaselineYears(yearsToCheck.filter(y => !valueBaseline?.[y]));
        // Calcular totalesByFilters para últimos 5 años
        if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
          const groupedData = groupDataByYearAndMonth(data.data);
          setTotalesByFilters(groupedData);
        }
      } catch (error) {
        console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [data, valueBaseline, configFormData, sharedProductionForms, sharedWorkersByPeriod]);

  const getValueBaseLine = async (year, type) => {
    try {
      const mode =
        type === "VPS" ? "standardized_production" : "standardized_billing";
      const response1 =
        await productionController.getTotalBaselineByYearAndSite(
          accessToken,
          site,
          year,
          mode
        );
      return response1.total;
    } catch (error) {
      return 1;
    }
  };

  // Función para agrupar datos por año y mes
  const groupDataByYearAndMonth = (dataArray) => {
    const grouped = {};

    dataArray.forEach((item) => {
      const year = String(item.year);
      const month = item.period; // period viene en formato "January", "February", etc.

      if (!grouped[year]) {
        grouped[year] = {};
      }

      if (!grouped[year][month]) {
        grouped[year][month] = { ...item };
      } else {
        // Combinar datos de registros duplicados del mismo mes
        Object.keys(item).forEach((key) => {
          if (item[key] !== undefined && item[key] !== null) {
            // Si el campo actual no existe o está vacío, usar el nuevo valor
            if (!grouped[year][month][key] || grouped[year][month][key] === null) {
              grouped[year][month][key] = item[key];
            }
          }
        });
      }
    });

    return grouped;
  };

  // Función que obtiene los campos fuente de un KPI basándose en socialFormulasIndicators
  const getKPISourceFields = useCallback((kpiName) => {
    const kpiSourceMap = {
      // KPIs calculados - agregaciones (siempre mostrar)
      'quantity_workers_kpis': null, // Agregación de todos los trabajadores
      'quantity_permanent_workers_kpis': null, // Agregación de trabajadores permanentes
      'quantity_temporary_workers_kpis': null, // Agregación de trabajadores temporales
      'quantity_workers_production_kpis': null, // Agregación de trabajadores de producción
      'quantity_workers_administrative_kpis': null, // Agregación de trabajadores administrativos
      'total_hours_worked_extra_and_normal_kpis': null, // Agregación de horas
      'average_female_workers_leadership_positions_kpis': null, // Calculado
      'average_male_workers_leadership_positions_kpis': null, // Calculado
      'average_female_total_kpis': null, // Calculado
      'average_male_total_kpis': null, // Calculado
      'quantity_workers_under_cct_kpis': ['workers_under_cct'], // Directo Soc16
      'percentage_workers_under_cct_kpis': null, // Calculado: Soc16 / KPIsoc1
      'total_accidents_kpis': null, // Agregación de accidentes

      // KPIs directos - mapeo 1:1 con campos
      'total_days_worked_kpis': ['days_worked'],
      'total_hours_worked_kpis': ['normal_hours_worked'],
      'total_fifty_percent_overtime_kpis': ['fifty_percent_overtime'],
      'total_one_hundred_percent_overtime_kpis': ['one_hundred_percent_overtime'],
      'total_work_accidents_with_sick_days_kpis': ['work_accidents_with_sick_days'],
      'total_accidents_first_aid_kpis': ['first_aid_without_sick_days'],
      'total_in_itine_accidents_kpis': ['in_itine_accidents'],
      'total_hours_training_social_issues_kpis': ['hours_training_social_issues'],
      'total_hours_training_hys_kpis': ['hours_training_hys'],
      'total_hours_environmental_awareness_training_kpis': ['hours_environmental_awareness_training'],
      'total_volunteer_hours_kpis': ['volunteer_hours'],
      'total_complaints_or_suggestions_received_kpis': ['complaints_or_suggestions_received'],
    };
    return kpiSourceMap[kpiName];
  }, []);

  // 🚀 Memoizar determinación de campos basándose en configFormData
  const determineFieldsKPIs = useMemo(() => {
    const fields = new Set();

    Object.keys(socialKPIsCodes).forEach((key) => {
      const sourceFields = getKPISourceFields(key);

      // Si no tiene campos fuente definidos (es calculado/agregado), agregarlo por defecto
      if (!sourceFields) {
        fields.add(key);
        return;
      }

      // Si hay configFormData, verificar si TODOS los campos fuente están activos
      if (configFormData?.fields && configFormData.fields.length > 0) {
        const allSourceFieldsActive = sourceFields.every(sourceFieldName => {
          const fieldConfig = configFormData.fields.find(f => f.name === sourceFieldName);
          return fieldConfig && fieldConfig.active !== false;
        });

        if (allSourceFieldsActive) {
          fields.add(key);
        }
      } else {
        // Si no hay configFormData, mostrar todos los campos (comportamiento por defecto)
        fields.add(key);
      }
    });

    return Array.from(fields);
  }, [configFormData, getKPISourceFields]);
  
    // Determinar los campos únicos de los formularios y si tienen código
    const determineFieldsCode = () => {
      const fields = new Set();
  
      Object.keys(socialCodes).forEach((key) => {
        fields.add(key);
      });
  
      return Array.from(fields);
    };
  

  const transformData1 = async (data) => {
    const totalsPerYear = {};
    const conceptsSet = [];
    const unitsCache = {};

    // Unidades configuradas en configFormData tienen prioridad máxima
    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    const fields = determineFieldsCode();
    const processedMonths = {}; // Trackear meses ya procesados por concepto/año

    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      return {};
    }

    const uniqueYears = [...new Set(data.data.map((item) => String(item.year)))];

    await Promise.all(
      uniqueYears.map(async (año) => {
        try {
          const units = await socialController.getLastUnitFieldsByYear(
            accessToken,
            site,
            año,
            fields
          );
          if (units && units.fields) {
            unitsCache[año] = units;
          }
        } catch (error) {}
      })
    );

    const consumptionPromises = data.data.map(async (item) => {
      const year = String(item.year);
      const month = item.period; // Mes del registro
      const units = unitsCache[year];
      if (!units) return;

      fields.forEach((concept) => {
        if (item[concept]) {
          conceptsSet.push(concept);
          if (!totalsPerYear[concept]) totalsPerYear[concept] = {};
          if (!totalsPerYear[concept][year]) {
            totalsPerYear[concept][year] = {
              total: 0,
              count: 0,
              unit: configUnits[concept] || units.fields[concept] || item[concept]?.unit || "",
            };
          }

          // Crear key única para evitar duplicados
          const monthKey = `${concept}_${year}_${month}`;
          if (!processedMonths[monthKey]) {
            processedMonths[monthKey] = true;

            const value = item[concept].value
              ? convertUnits(
                  item[concept].value,
                  item[concept].unit,
                  totalsPerYear[concept][year].unit
                )
              : 0;

            totalsPerYear[concept][year].total += value;
            totalsPerYear[concept][year].count += 1;
          }
        }
      });
    });

    await Promise.all(consumptionPromises);

    Object.keys(totalsPerYear).forEach((concept) => {
      Object.keys(totalsPerYear[concept]).forEach((year) => {
        const { total, count } = totalsPerYear[concept][year];
        if (count > 0) {
          totalsPerYear[concept][year].average = total / count;
        }
      });
    });

    return totalsPerYear;
  };

  const calculateDataFinal = async (totales) => {
    const totalesFinal = { ...totales };
    const conceptsSet = [];

    if (totales !== undefined) {
      for (let item in totales) {
        if (totales.hasOwnProperty(item)) {
          for (let year in totales[item]) {
            const valueB = valueBaseline[year];
            if (totales[item].hasOwnProperty(year)) {
              const fields = determineFieldsKPIs; // 🚀 Usar directamente el array memoizado

              fields.forEach((concept) => {
                if (!totalesFinal[concept]) {
                  totalesFinal[concept] = {};
                  conceptsSet.push(concept);
                }

                if (!totalesFinal[concept][year]) {
                  totalesFinal[concept][year] = {};
                }

                totalesFinal[concept][year] = socialFormulasIndicators(
                  concept,
                  year,
                  totalesFinal,
                  valueB === 0 ? 1 : valueB
                );
              });
            }
          }
        }
      }
    }

    setConceptos(removeNulls(totalesFinal, conceptsSet));
    setTotales(totalesFinal);
  };


  // Función para obtener los valores de un año y mes específicos (period en texto)
    const getDataByYearAndMonth = async (data, year, month) => {
      const results = {};
      const fields = determineFieldsCode();
      const unitsCache = {};

      const configUnits = {};
      if (configFormData?.fields && configFormData.fields.length > 0) {
        configFormData.fields.forEach((field) => {
          if (field.name && field.unit) {
            configUnits[field.name] = field.unit;
          }
        });
      }

      if (!data || !data || Object.keys(data).length === 0) {
        return {};
      }
  
      // Diccionario de meses
      const monthMap = {
        January: "01",
        February: "02",
        March: "03",
        April: "04",
        May: "05",
        June: "06",
        July: "07",
        August: "08",
        September: "09",
        October: "10",
        November: "11",
        December: "12",
      };
  
      const yearStr = String(year);
      const monthStr =
        typeof month === "string"
          ? monthMap[month] || ""
          : String(month).padStart(2, "0");
  
      if (!monthStr) {
        console.warn("Mes inválido o no reconocido:", month);
        return {};
      }
  
      const yearData = data[yearStr];
      if (!Array.isArray(yearData)) {
        console.warn("No hay datos para el año:", yearStr);
        return {};
      }
  
      const filteredData = yearData.filter(
        (item) => monthMap[item.period] === monthStr
      );
  
      if (filteredData.length === 0) {
        console.warn(`No hay datos para ${month} ${yearStr}`);
        return {};
      }
  
      // Procesar resultados por campo
      filteredData.forEach((item) => {
        const units = unitsCache[yearStr];
  
        fields.forEach((concept) => {
          if (item[concept]) {
            if (!results[concept]) {
              results[concept] = {
                total: 0,
                unit: configUnits[concept] || units?.fields[concept] || item[concept]?.unit || "",
              };
            }
  
            results[concept].total += item[concept].value
              ? convertUnitsFinal(
                  item[concept].value,
                  item[concept].unit,
                  results[concept].unit,
                  concept
                )
              : 0;
          }
        });
      });
  
      // ==========================
      // 🔹 Calcular energía total (sin incluir costos)
      // ==========================
  
      // Lista de conceptos energéticos (sin costos)
      const socialConcepts = Object.keys(socialCodes).filter(
        (key) => !key.includes("cost")
      );
  
      let totalSocialMonth = 0;
      let socialUnit = "";
  
      socialConcepts.forEach((concept) => {
        if (results[concept] && typeof results[concept].total === "number") {
          totalSocialMonth += results[concept].total;
          socialUnit = results[concept].unit || socialUnit;
        }
      });
  
      results.total_social_month = {
        total: totalSocialMonth,
        unit: socialUnit || "MWh",
      };
  
  
          // ==========================
      // 🔹 Calcular renovables
      // ==========================
  
          // ==========================
      // 🔹 Calcular electrica
      // ==========================
      return results;
    };
  
    // ==========================
    // 🔹 Calcular ultimos 3 años
    // ==========================
  
    const getLastThreeYearsData = async (totalesByFilters) => {
      const currentYear = new Date().getFullYear();
      const lastThreeYears = [currentYear - 2, currentYear - 1, currentYear];
  
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
  
      const resultsByYear = {};
  
      for (const year of lastThreeYears) {
        resultsByYear[year] = {};
  
        for (const month of months) {
          const data = await getDataByYearAndMonth(totalesByFilters, year, month);
          resultsByYear[year][month] = data;
        }
      }
  
      return resultsByYear;
    };
   
    const removeNulls = (data, concepts) => {
      // console.log("🔍 Verificando KPIs con filas vacías en Social...");
      // console.log("📊 Total de KPIs antes del filtrado:", concepts.length);
      const removedKPIs = [];

      // Iterar sobre las claves externas
      for (let key in data) {
        if (data.hasOwnProperty(key)) {
          // Verificar si todos los valores internos son 0
          const allZero = Object.values(data[key]).every(
            (value) =>
              value.total === 0 || value.total === undefined || isNaN(value.total)
          );

          // Si todos los valores son 0, eliminar la clave externa
          if (allZero) {
           // console.log(`❌ KPI eliminado (fila vacía): ${key}`);
            removedKPIs.push(key);
            concepts = concepts.filter((item) => item !== key);
          }
        }
      }

      // console.log("📊 Total de KPIs después del filtrado:", concepts.length);
      // console.log("🗑️ KPIs eliminados por tener fila vacía:", removedKPIs);
      // console.log("✅ KPIs visibles:", concepts);

      return concepts;
    };
  
  async function calcularProduccionPorMesPorAño(documentosPorAño, baselines) {
    const resultado = {};
    for (const [year, docs] of Object.entries(documentosPorAño)) {
      const baselineType = baselines[year];
      if (!baselineType) continue;
      const fieldName = baselineType === "VPS" ? "standardized_production" : "standardized_billing";
      resultado[year] = {};
      for (const doc of docs) {
        const mes = doc.period;
        if (!mes) continue;
        resultado[year][mes] = doc[fieldName]?.value || 0;
      }
    }
    return resultado;
  }

  const summaryRef = useRef();
  const tableGrafRef = useRef();
  const comparisonChartRef = useRef();
  const workersChartRef = useRef();
  const productivityChartRef = useRef();

  const yearsWithData = useMemo(() => {
    if (!data?.years || !conceptos || conceptos.length === 0) return [];
    return [...data.years]
      .sort((a, b) => b - a)
      .filter(year =>
        conceptos.some(item => (totales[item]?.[year]?.total || 0) !== 0)
      );
  }, [data, conceptos, totales]);

  // Umbral de magnitud para inferir Regla 1 (entero) en filas sin unidad
  // explícita de conteo/porcentaje — mismo criterio que la tabla en
  // pantalla (ver resolveRowRule más abajo en este archivo).
  const PDF_WHOLE_NUMBER_THRESHOLD = 100;

  // AJUSTES DE MAGNITUDES NUMÉRICAS aplicado a la tabla del PDF (antes
  // usaba `toFixed(2)` fijo + coma decimal, sin separador de miles ni
  // distinción por tipo de indicador). Replica la misma inferencia de
  // regla por fila que ya usa la tabla en pantalla (resolveRowRule /
  // formatByRowRule): "%" -> Regla 3 SIN el símbolo "%" (la columna
  // "Unidades" ya lo indica, repetirlo en cada celda es redundante),
  // conteos (Unid./uni) -> Regla 1, el resto según su magnitud (Regla 1
  // si es grande, si no Regla 2+).
  const formatSocialCellForPdf = (value, item, unit) => {
    if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
    let formatted;
    if (unit === "%") formatted = formatMagnitude(value * 100, 2);
    else if (unit === "Unid." || unit === "uni") formatted = formatWhole(value);
    else {
      const rowValues = Object.values(totales[item] || {})
        .map((entry) => entry?.total)
        .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
        .map(Math.abs);
      const isWhole = rowValues.length > 0 && Math.max(...rowValues) >= PDF_WHOLE_NUMBER_THRESHOLD;
      formatted = isWhole ? formatWhole(value) : formatMagnitudeAdaptive(value);
    }
    // Esta celda la dibuja autoTable con doc.text() nativo (no SVG) — el
    // espacio fino (U+202F) que usan formatWhole/formatMagnitudeAdaptive
    // no existe en la codificación WinAnsi de las fuentes estándar de
    // jsPDF: se ve como "/" y además rompe el kerning del resto del texto
    // (dígitos separados entre sí). Mismo problema ya resuelto en
    // GEIPDFReport.js — acá se reemplaza por un espacio ASCII normal.
    return formatted.replace(/ /g, " ");
  };

    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (
      !data ||
      !data.years ||
      data.years.length === 0 ||
      !conceptos ||
      conceptos.length === 0
    ) {
      return (
        <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
          <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
            <Icon name="exclamation triangle" size="huge" color="gray" />
          </div>
          <div
            style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}
          >
            {t("no_data_available") || "No hay datos disponibles"}
          </div>
        </div>
      );
    } else {
      const exportYears = data?.years ? [...data.years].sort((a, b) => Number(b) - Number(a)).slice(0, 5) : [];
      return (
        <div className="list-social-forms">

          {/* Aviso cuando hay años sin baseline VPS/VFS */}
          {missingBaselineYears.length > 0 && (
            <div style={{
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "6px",
              padding: "10px 16px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "0.92em",
            }}>
              <Icon name="warning sign" color="yellow" />
              <span>
                Algunos indicadores (KPIs) para los años <strong>{missingBaselineYears.join(", ")}</strong> mostrarán valor 0. Configurá el volumen producido (VPS) o el volumen facturado (VFS) como línea base para verlos correctamente.{" "}
                <Link to="/admin/data/productionforms" style={{ color: "#856404", fontWeight: "bold" }}>
                  Configurar línea base →
                </Link>
              </span>
            </div>
          )}

          {/* Sección de Resumen */}
          <div className="social-summary-section" ref={summaryRef}>
            <SocialSummary
              filters={filters}
              site={site}
              baseline={baseline}
              valueBaseline={valueBaseline}
              socialController={Socialform}
              totales={totalesByFilters} // ✅ datos por año/mes
              totalesKPIs={totales} // ✅ KPIs calculados
              selectorSlot={<YearMonthSelector onChange={setFilters} availableYears={yearsWithData} />}
              exportSlot={
                <KPIExportIconButton
                  title={t("social") || "Social"}
                  conceptos={conceptos}
                  years={exportYears}
                  totales={totales}
                  codes={socialKPIsCodes}
                  t={t}
                  codes={socialKPIsCodes}
                  formatCellValue={formatSocialCellForPdf}
                  chartRefs={[
                    { ref: summaryRef, label: t("summary") || "Resumen" },
                  ]}
                  renderCustomCharts={(doc, exportCtx) =>
                    drawSocialCustomCharts(doc, exportCtx, {
                      totales,
                      filters,
                      baseline,
                      resultsByYear: totalesByFilters,
                      resultsWorkers: totalesWorkersByFilters,
                      resultsProductions: totalesProductionsByFilters,
                      productionController,
                      accessToken,
                      site,
                      t,
                    })
                  }
                  siteName={siteName}
                />
              }
            />
          </div>
          <div className="social-tablegraf-section" ref={tableGrafRef}>
            <TableGraf
              filters={filters}
              totales={totales}
              baseline={baseline}
              valueBaseline={valueBaseline}
              site={site}
              t={t}
            />
          </div>
          <CollapsibleSection title={t("production_comparison_last_3_years")} className="social-comparison-section">
            <SocialComparisonChart
              baseline={baseline}
              site={site}
              t={t}
            />
          </CollapsibleSection>
          <CollapsibleSection title={t("workers_comparison_last_3_years")} className="social-monthly-section" ref={workersChartRef}>
            <SocialMonthlyChart resultsByYear={totalesByFilters} t={t} />
          </CollapsibleSection>
          <CollapsibleSection title={t("productivity_comparison_last_3_years")} className="social-productivity-section" ref={productivityChartRef}>
            <ProductivityChart
              baseline={baseline}
              site={site}
              resultsByYear={totalesByFilters}
              resultsWorkers={totalesWorkersByFilters}
              resultsProductions={totalesProductionsByFilters}
              t={t}
            />
          </CollapsibleSection>
          {/* //<EnergyChart filters={filters} data={data} /> */}
          {/*} <EnergyIntensityChart  totales={totales} />*/}

          <TableYears
            filters={filters}
            data={data}
            accessToken={accessToken}
            t={t}
            conceptos={conceptos}
            totales={totales}
            language={language}
            indicatorsLineBase={indicatorsLineBase}
            valueBaseline={valueBaseline}
            defaultOpen={true}
          />

          {/* Sección de Valores Intermedios */}
          {/* <IntermediateValuesDisplay
            resultsByYear={totalesByFilters}
            resultsWorkers={totalesWorkersByFilters}
            resultsProductions={totalesProductionsByFilters}
            t={t}
          /> */}
        </div>
      );
    }
  }
  
  function TableYears(props) {
    const {
      data,
      conceptos,
      totales,
      t,
      language,
      valueBaseline,
      indicatorsLineBase,
      filters,
      defaultOpen = true,
    } = props;

    const { year, month } = filters || {};
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [years, setYears] = useState([]);
    const [months, setMoths] = useState([]);
    const [listConceptos, setListConceptos] = useState([]);
  
    useEffect(() => {
      console.log("📊 TableGraf filtrando con:", { year, month });
    }, [year, month]);
  
    useEffect(() => {
      (async () => {
        try {
          setYears(data.years ? data.years.sort((a, b) => b - a) : []);
        } catch (error) {
          console.error(error);
        }
      })();
    }, [data]);
  
    useEffect(() => {
      (async () => {
        try {
          setListConceptos(conceptos);
        } catch (error) {
          console.error(error);
        }
      })();
    }, [conceptos]);

    // Función para obtener el icono de aumento o disminución
    const getComparativeIcon = (currentYear, previousYear) => {
      if (previousYear !== null) {
        if (currentYear > previousYear) {
          return <Icon disabled name="angle up" color="green" />; // Aumento
        } else if (currentYear < previousYear) {
          return <Icon disabled name="angle down" color="red" />; // Disminución
        } else if (currentYear === previousYear) {
          return <span style={{ color: "blue", fontWeight: "bold" }}>=</span>;
        }
      }
      return null; // Sin cambio
    };
  
    // Función para obtener el icono de aumento o disminución
    const getIndicators = (item) => {
      let result = indicatorsLineBase.find((value) => value.name === item);
      if (result) {
        return result?.yearBaseline || 0;
      }
      return 0; // Sin cambio
    };
  
    // Obtener la unidad del primer año disponible para un concepto
    const getUnitForItem = (item) => {
      const firstYear = years.find((y) => totales[item]?.[y]?.unit);
      return firstYear ? totales[item][firstYear].unit : "";
    };

    // Umbral de magnitud para inferir Regla 1 (entero) en filas sin regla explícita
    const WHOLE_NUMBER_THRESHOLD = 100;

    // Determina la regla de la fila: los conteos (unidad "Unid."/"uni") siempre son
    // Regla 1 (entero); el resto se infiere por magnitud (Regla 1 si es grande, si
    // no Regla 2/2+ adaptativa)
    const resolveRowRule = (values, isCount, isPercentage) => {
      if (isPercentage) return "percentage";
      if (isCount) return "whole";
      const nonZero = values
        .filter((v) => typeof v === "number" && !isNaN(v) && v !== 0)
        .map(Math.abs);
      if (nonZero.length === 0) return "adaptive";
      return Math.max(...nonZero) >= WHOLE_NUMBER_THRESHOLD ? "whole" : "adaptive";
    };

    // Formatea un valor según la regla resuelta para su fila
    const formatByRowRule = (rule, value) => {
      if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
      if (rule === "percentage") return formatWhole(value * 100);
      return rule === "whole" ? formatWhole(value) : formatMagnitudeAdaptive(value);
    };

    // Renderiza una píldora de comparativa rojo/verde
    const renderPill = (value, prevValue, rule) => {
      const numValue = value != null ? Number(value) : null;
      const numPrev = prevValue != null ? Number(prevValue) : null;

      if (numValue == null || numValue === 0) {
        return <span className="gei-value-pill pill-empty" />;
      }

      let pillClass;
      if (numPrev == null || numPrev === 0) {
        pillClass = "pill-neutral";
      } else if (numValue > numPrev) {
        pillClass = "pill-up";
      } else if (numValue < numPrev) {
        pillClass = "pill-down";
      } else {
        pillClass = "pill-neutral";
      }

      return (
        <span className={`gei-value-pill ${pillClass}`}>
          {formatValue(formatByRowRule(rule, numValue), language)}
        </span>
      );
    };

    return (
      <>
        <div className="kpi-table-section">
        <div className="section-content">
        <Table celled structured size="small" className="gei-kpi-table" unstackable>
          <Table.Header>
            <Table.Row
              onClick={() => setIsOpen(!isOpen)}
              style={{ cursor: "pointer" }}
            >
              <Table.HeaderCell textAlign="center">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="center">
                {t("indicators")}
              </Table.HeaderCell>
              {years.map((year, index) => (
                <Table.HeaderCell textAlign="center" key={index}>
                  {year}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell textAlign="center">
                {t("LB")}
              </Table.HeaderCell>
              <Table.HeaderCell
                textAlign="center"
                style={{ width: "200px" }}
              >
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {t("units")}
                  <Icon name={isOpen ? "chevron up" : "chevron down"} style={{ marginLeft: "auto" }} />
                </span>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          {isOpen && <Table.Body>
            {conceptos.map((item, index) => {
              // Recopilar todos los valores de la fila para determinar decimales consistentes
              const rowValues = years
                .map((y) => totales[item]?.[y]?.total || 0)
                .filter((v) => typeof v === "number" && !isNaN(v));
              const lbValue = totales[item]?.[getIndicators(item)]?.total || 0;
              if (typeof lbValue === "number" && !isNaN(lbValue)) rowValues.push(lbValue);
              const unit = getUnitForItem(item);
              const isCount = unit === "Unid." || unit === "uni";
              const isPercentage = unit === "%";
              const rowRule = resolveRowRule(rowValues, isCount, isPercentage);

              return (
              <React.Fragment key={index}>
                <Table.Row>
                  <Table.Cell data-label={t("code")}>{socialKPIsCodes[item]}</Table.Cell>
                  <Table.Cell data-label={t("indicators")}>{t(item)}</Table.Cell>
                  {years.map((year, i) => {
                    if (totales[item]) {
                      const currentValue = totales[item][year]?.total || 0;
                      const previousValue = totales[item][year - 1]?.total || 0;
                      const pill = renderPill(currentValue, previousValue, rowRule);
                      return (
                        <Table.Cell key={i} textAlign="center" data-label={year}>
                          {pill}
                        </Table.Cell>
                      );
                    }
                    return <Table.Cell key={i} data-label={year}>{""}</Table.Cell>;
                  })}
                  <Table.Cell data-label={t("LB")}>
                    {(() => {
                      const lbVal = totales[item][getIndicators(item)]?.total || 0;
                      const lbFormatted = formatByRowRule(rowRule, lbVal);
                      if (isCount && lbFormatted !== "-") {
                        return (
                          <Popup
                            content={formatValue(lbFormatted, language)}
                            trigger={<span style={{ cursor: "default" }}>{formatValue(lbFormatted, language)}</span>}
                            size="small"
                            position="top center"
                          />
                        );
                      }
                      return formatValue(lbFormatted, language);
                    })()}
                  </Table.Cell>
                  <Table.Cell data-label={t("units")}>
                    {unit}
                  </Table.Cell>

                  {/* <Table.Cell>
                    {convertUnitsFinal(
                      totales[item][getIndicators(item)]?.total || 0,
                      totales[item][getIndicators(item)]?.unit,
                      "MWh",
                      item
                    ) || 0}
                  </Table.Cell> */}
                </Table.Row>
              </React.Fragment>
              );
            })}
          </Table.Body>}
        </Table>
        </div>
        </div>
      </>
    );
  }

  export const YearMonthSelector = ({ onChange, availableYears = [] }) => {
    const currentYear = new Date().getFullYear();

    const years = availableYears.length > 0
      ? [...availableYears].sort((a, b) => b - a).map(y => ({ key: y, text: y, value: y }))
      : [
          { key: currentYear, text: currentYear, value: currentYear },
          { key: currentYear - 1, text: currentYear - 1, value: currentYear - 1 },
          { key: currentYear - 2, text: currentYear - 2, value: currentYear - 2 },
          { key: currentYear - 3, text: currentYear - 3, value: currentYear - 3 },
          { key: currentYear - 4, text: currentYear - 4, value: currentYear - 4 },
        ];

    const [selectedYear, setSelectedYear] = useState(currentYear);

    useEffect(() => {
      if (availableYears && availableYears.length > 0 && !availableYears.includes(selectedYear)) {
        const sorted = [...availableYears].sort((a, b) => b - a);
        const newYear = sorted[0];
        setSelectedYear(newYear);
        onChange({ year: newYear });
      }
    }, [availableYears]);

    const handleYearChange = (e, { value }) => {
      setSelectedYear(value);
      onChange({ year: value });
    };

    // const handleMonthChange = (e, { value }) => {
    //   setSelectedMonth(value);
    //   onChange({ year: selectedYear, month: value });
    // };

    return (
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
        <Dropdown
          placeholder="Año"
          selection
          options={years}
          value={selectedYear}
          onChange={handleYearChange}
          className="year-filter-dropdown"
        />
        {/* <Dropdown
          placeholder="Mes"
          selection
          options={months}
          value={selectedMonth}
          onChange={handleMonthChange}
        /> */}
      </div>
    );
  };

function SocialComparisonChart({ baseline, site, t }) {
  const windowWidth = useWindowWidth();
  const { accessToken } = useAuth();

  // Estado para los datos mensuales de producción
  const [monthlyProductionData, setMonthlyProductionData] = useState({});

  // Obtener los datos mensuales de producción
  useEffect(() => {
    fetchProductionMonthlyData(productionController, accessToken, site, baseline).then(
      setMonthlyProductionData,
    );
  }, [baseline, site, accessToken]);

  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      chartHeight: isSmallMobile ? 250 : isMobile ? 280 : 300,
    };
  }, [windowWidth]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Obtener los últimos 3 años
  const currentYear = new Date().getFullYear();
  const lastThreeYears = Array.from({ length: 3 }, (_, i) =>
    String(currentYear - i),
  );

  // ✅ Calcular los datos solo cuando cambien monthlyProductionData
  const { barData, lineData, yearColorMap } = useMemo(
    () => buildProductionComparisonData(monthlyProductionData, baseline, t),
    [monthlyProductionData, baseline, t],
  );

  // const config = {
  //   xField: "month",
  //   legend: {
  //     position: "top",
  //     itemName: {
  //       style: {
  //         fontSize: responsiveConfig.fontSize.medium,
  //       },
  //     },
  //   },
  //   autoFit: true,
  //   children: [
  //     {
  //       data: barData,
  //       type: "interval",
  //       yField: "value",
  //       colorField: "year",
  //       seriesField: "year",
  //       group: true,
  //       interaction: { elementHighlight: { background: true } },
  //     },
  //     {
  //       data: lineData,
  //       type: "line",
  //       yField: "value",
  //       colorField: "year",
  //       smooth: true,
        
  //       style: { lineWidth: 2, lineDash: [4, 4] },
  //       axis: { y: { position: "right" } },
  //     },
  //   ],
  //   tooltip: {
  //     shared: true,
  //     title: (d) => d.month,
  //     items: [
  //       (datum) => ({
  //         name: datum.isAverage
  //           ? `Promedio ${datum.year} (${datum.type || "VPS"})`
  //           : `Año ${datum.year} (${datum.type || "VPS"})`,
  //         value: datum.value
  //           ? datum.value.toLocaleString("es-ES", {
  //               minimumFractionDigits: 2,
  //               maximumFractionDigits: 2,
  //             })
  //           : "0",
  //       }),
  //     ],
  //   },

  //   height: responsiveConfig.chartHeight,
  //   xAxis: {
  //     title: {
  //       style: {
  //         fontSize: responsiveConfig.fontSize.medium,
  //       },
  //     },
  //     label: {
  //       style: {
  //         fontSize: responsiveConfig.fontSize.small,
  //       },
  //       autoRotate: responsiveConfig.isMobile ? true : false,
  //       autoHide: responsiveConfig.isMobile,
  //     },
  //   },
  //   yAxis: {
  //     title: {
  //       style: {
  //         fontSize: responsiveConfig.fontSize.medium,
  //       },
  //     },
  //     label: {
  //       style: {
  //         fontSize: responsiveConfig.fontSize.small,
  //       },
  //       formatter: (v) => {
  //         if (responsiveConfig.isMobile && v >= 1000) {
  //           return `${(v / 1000).toFixed(1)}K`;
  //         }
  //         return v;
  //       },
  //     },
  //   },
  // };
  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {lastThreeYears.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(lastThreeYears);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredBarData = useMemo(() =>
    barData.filter(d => activeYears.includes(String(d.year))),
  [barData, activeYears]);

  const filteredLineData = useMemo(() =>
    lineData.filter(d => activeYears.includes(String(d.year))),
  [lineData, activeYears]);

  // Escala Y explícita y compartida entre barras y línea de promedio ("sync" no alineaba bien)
  const combinedMax = Math.max(
    0,
    ...filteredBarData.map((d) => d.value || 0),
    ...filteredLineData.map((d) => d.value || 0),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  const sharedYScale = { domainMin: 0, domainMax: yDomainMax, nice: true };

  const config = {
    xField: "month",
    legend: false,
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false },
      y: { title: false, grid: true, labelFormatter: (v) => formatWhole(v) },
    },
    tooltip: {
      shared: true,
      title: (d) => d.month,
      items: [
        (datum) => ({
          name: datum.isAverage
            ? `Promedio ${datum.year} (${datum.type || "VPS"})`
            : `Año ${datum.year} (${datum.type || "VPS"})`,
          value: formatWhole(datum.value),
        }),
      ],
    },
    children: [
      {
        data: filteredBarData,
        type: "interval",
        yField: "value",
        colorField: "year",
        group: true,
        style: { maxWidth: 80 },
        scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year",
        axis: { y: false },
        scale: { y: sharedYScale },
        style: { lineWidth: 2,
        lineDash: [4, 8]},
      },
    ],
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {barData.length > 0 && <CustomLegend />}
      {barData.length ? (
        <div style={{ height: 350 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 350, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

function SocialMonthlyChart({ resultsByYear, t }) {
  const windowWidth = useWindowWidth();

  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      chartHeight: isSmallMobile ? 250 : isMobile ? 280 : 300,
    };
  }, [windowWidth]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Obtener los últimos 3 años
  const currentYear = new Date().getFullYear();
  const lastThreeYears = Array.from({ length: 3 }, (_, i) =>
    String(currentYear - i),
  );

  // ✅ Calcular los datos solo cuando cambien resultsByYear
  const { barData, lineData, yearColorMap } = useMemo(
    () => buildWorkersComparisonData(resultsByYear, t),
    [resultsByYear, t],
  );
  const CustomLegend = () => (
    <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
      {lastThreeYears.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );

  const [activeYears, setActiveYears] = useState(lastThreeYears);

  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };

  const filteredBarData = useMemo(() =>
    barData.filter(d => activeYears.includes(String(d.year))),
  [barData, activeYears]);

  const filteredLineData = useMemo(() =>
    lineData.filter(d => activeYears.includes(String(d.year))),
  [lineData, activeYears]);

  // Escala Y explícita y compartida entre barras y línea de promedio ("sync" no alineaba bien)
  const combinedMax = Math.max(
    0,
    ...filteredBarData.map((d) => d.value || 0),
    ...filteredLineData.map((d) => d.value || 0),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  const sharedYScale = { domainMin: 0, domainMax: yDomainMax, nice: true };

  const config = {
    xField: "month",
    legend: false,
    scale: {
      color: {
        domain: Object.keys(yearColorMap),
        range: Object.values(yearColorMap),
      },
    },
    tooltip: {
      items: [
        (item) => {
          const marker = item.type?.includes("Promedio") ? "- -" : "";
          return {
            name: `${marker} ${item.type}`,
            value: formatWhole(item.value),
          };
        },
      ],
    },
    axis: {
      x: { title: false, labelAutoHide: false, labelAutoRotate: false },
      y: { title: false, grid: true, labelFormatter: (v) => formatWhole(v) },
    },
    children: [
      {
        data: filteredBarData,
        type: "interval",
        yField: "value",
        colorField: "year",
        seriesField: "year",
        group: true,
        style: { maxWidth: 80 },
        scale: { color: ({ year }) => yearColorMap[year], y: sharedYScale },
        interaction: { elementHighlight: { background: true } },
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year",
        style: {lineWidth: 2,
        lineDash: [4, 8] },
        axis: { y: false },
        scale: { y: sharedYScale },
      },
    ],
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {barData.length > 0 && <CustomLegend />}
      {barData.length ? (
        <div style={{ height: 350 }}>
          <DualAxes {...config} />
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 350, color: "#999" }}>
          No hay datos disponibles
        </div>
      )}
    </div>
  );
}

  function ProductivityChart({ baseline, resultsWorkers, resultsProductions, t }) {

    const windowWidth = useWindowWidth();

    const responsiveConfig = useMemo(() => {
      const isMobile = windowWidth <= 768;
      const isSmallMobile = windowWidth <= 480;

      return {
        isMobile,
        isSmallMobile,
        fontSize: {
          large: isSmallMobile ? 12 : isMobile ? 14 : 16,
          medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
          small: isSmallMobile ? 9 : isMobile ? 10 : 11,
        },
        chartHeight: isSmallMobile ? 250 : isMobile ? 280 : 300,
      };
    }, [windowWidth]);

    const months = useMemo(() => [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ], []);

    // Obtener los últimos 3 años
    const currentYear = new Date().getFullYear();
    const lastThreeYears = useMemo(
      () => Array.from({ length: 3 }, (_, i) => String(currentYear - i)),
      [currentYear]
    );

    // Productividad mensual = producción_mes / trabajadores_mes
    const { consumptionData, lineData, yearColorMap } = useMemo(
      () => buildProductivityData(baseline, resultsWorkers, resultsProductions, t),
      [baseline, resultsWorkers, resultsProductions, t],
    );
    const CustomLegend = () => (
      <div style={{ display: "flex", justifyContent: "flex-start", gap: "10px", marginBottom: "20px", paddingLeft: "5px", flexWrap: "wrap" }}>
        {lastThreeYears.map((year) => {
          const isActive = activeYears.includes(year);
          return (
            <div
              key={year}
              onClick={() => toggleYear(year)}
              style={{
                backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6",
                color: isActive ? "white" : "#9ca3af",
                padding: "4px 20px",
                borderRadius: "20px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
                boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "100px",
              }}
            >
              {year}
            </div>
          );
        })}
      </div>
    );

    const [activeYears, setActiveYears] = useState(lastThreeYears);

    const toggleYear = (year) => {
      if (activeYears.includes(year)) {
        setActiveYears(activeYears.filter(y => y !== year));
      } else {
        setActiveYears([...activeYears, year]);
      }
    };

    const filteredConsumptionData = useMemo(() =>
      consumptionData.filter(d => activeYears.includes(String(d.year))),
    [consumptionData, activeYears]);

    const filteredLineData = useMemo(() =>
      lineData.filter(d => activeYears.includes(String(d.year))),
    [lineData, activeYears]);

    // Escala Y explícita y compartida entre Consumo y Promedio ("sync" no alineaba bien)
    const combinedMax = Math.max(
      0,
      ...filteredConsumptionData.map((d) => d.value || 0),
      ...filteredLineData.map((d) => d.value || 0),
    );
    const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
    const sharedYScale = { domainMin: 0, domainMax: yDomainMax, nice: true };

    const config = {
      xField: "month",
      legend: false,
      scale: {
        color: {
          domain: Object.keys(yearColorMap),
          range: Object.values(yearColorMap),
        },
      },
      tooltip: {
        items: [
          (item) => {
            const marker = item.type?.includes("Promedio") ? "- -" : "";
            return {
              name: `${marker} ${item.type}`.trim(),
              value: formatWhole(item.value),
            };
          },
        ],
      },
      axis: {
        x: { title: false, labelAutoHide: false, labelAutoRotate: false },
        y: { title: false, grid: true, labelFormatter: (v) => formatWhole(v) },
      },
      children: [
        {
          data: filteredConsumptionData,
          type: "line",
          yField: "value",
          colorField: "year",
          style: { lineWidth: 4 },
          scale: { y: sharedYScale },
          interaction: { elementHighlight: { background: true } },
        },
        {
          data: filteredLineData,
          type: "line",
          yField: "value",
          colorField: "year",
          style: { lineWidth: 2, lineDash: [4, 8] },
          axis: { y: false },
          scale: { y: sharedYScale },
        },
      ],
    };

    return (
      <div style={{ marginTop: 20, marginBottom: 40 }}>
        {consumptionData.length > 0 && <CustomLegend />}
        {consumptionData.length ? (
          <div style={{ height: 350 }}>
            <DualAxes {...config} />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 350, color: "#999" }}>
            No hay datos disponibles
          </div>
        )}
      </div>
    );
  }

  // Componente para mostrar valores intermedios utilizados en los cálculos
  function IntermediateValuesDisplay({ resultsByYear, resultsWorkers, resultsProductions, t }) {
    const [isOpen, setIsOpen] = useState(false);

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Obtener todos los años disponibles y ordenarlos de mayor a menor
    const availableYears = Object.keys(resultsByYear || {}).sort(
      (a, b) => b - a
    );

    // Limitar a los últimos 5 años
    const lastFiveYears = availableYears.slice(0, 5);

    return (
      <div
        style={{
          margin: "20px 0",
          padding: "15px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #dee2e6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            padding: "10px 0",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <h4 style={{ margin: 0, color: "#495057" }}>
            <Icon name={isOpen ? "chevron down" : "chevron right"} />
            Valores intermedios para verificación de cálculos - Últimos 5 años
          </h4>
          <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
            {isOpen ? "Haz clic para ocultar" : "Haz clic para ver detalles"}
          </span>
        </div>

        {isOpen && (
          <>
            {lastFiveYears.map((year) => (
              <div key={year} style={{ marginBottom: "30px" }}>
                <h5
                  style={{
                    marginTop: "20px",
                    marginBottom: "10px",
                    color: "#495057",
                  }}
                >
                  Año {year}
                </h5>
                <Table celled size="small" style={{ fontSize: "0.9em" }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell textAlign="center">Mes</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        Producción (VPS/VFS)
                      </Table.HeaderCell>
                      <Table.HeaderCell textAlign="center">
                        Trabajadores
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {months.map((month) => {
                      const workers = resultsWorkers?.[year]?.[month] || 0;
                      const production = resultsProductions?.[year]?.[month] || 0;

                      return (
                        <Table.Row key={month}>
                          <Table.Cell textAlign="center">
                            {t ? t(month) : month}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {production > 0 ? production.toFixed(2) : "-"}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {workers > 0 ? Math.round(workers) : "-"}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                    <Table.Row
                      style={{ fontWeight: "bold", backgroundColor: "#e9ecef" }}
                    >
                      <Table.Cell textAlign="center">Total</Table.Cell>
                      <Table.Cell textAlign="center">
                        {(() => {
                          const total = months.reduce((sum, month) => sum + (resultsProductions?.[year]?.[month] || 0), 0);
                          return total > 0 ? total.toFixed(2) : "-";
                        })()}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {Math.round(months.reduce((sum, month) => {
                          return sum + (resultsWorkers?.[year]?.[month] || 0);
                        }, 0))}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row
                      style={{ fontWeight: "bold", backgroundColor: "#d1ecf1", color: "#0c5460" }}
                    >
                      <Table.Cell textAlign="center">Promedio</Table.Cell>
                      <Table.Cell textAlign="center">
                        {(() => {
                          const values = months.map((m) => resultsProductions?.[year]?.[m] || 0).filter((v) => v > 0);
                          const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
                          return avg > 0 ? avg.toFixed(2) : "-";
                        })()}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {(() => {
                          const values = Object.values(resultsWorkers?.[year] || {});
                          const count = values.filter((v) => v > 0).length;
                          const avg = count > 0 ? values.reduce((a, b) => a + b, 0) / count : 0;
                          return Math.round(avg);
                        })()}
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            ))}

            <div
              style={{
                fontSize: "0.85em",
                color: "#6c757d",
                marginTop: "10px",
                textAlign: "center",
              }}
            >
              <strong>Nota:</strong> Estos valores se utilizan como denominadores
              en los cálculos de intensidad. Producción (VPS/VFS) para intensidad
              industrial y Trabajadores para intensidad doméstica.
            </div>
          </>
        )}
      </div>
    );
  }
